// src/router/index.js
import Vue from 'vue'
import Router from 'vue-router'
import Layout from '@/layout'
import store from '@/store'
import { Message } from 'element-ui'
import { getToken } from '@/utils/auth'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import getPageTitle from '@/utils/get-page-title'

Vue.use(Router)

// 进度条配置
NProgress.configure({ showSpinner: false })

// 公共路由
export const constantRoutes = [
  {
    path: '/login',
    component: () => import('@/views/Login'),
    hidden: true
  },
  {
    path: '/404',
    component: () => import('@/views/error-page/404'),
    hidden: true
  },
  {
    path: '/change-password',
    component: Layout,
    hidden: true,
    children: [
      {
        path: '',
        component: () => import('@/views/change-password/index'),
        name: 'ChangePassword',
        meta: { title: '修改密码' }
      }
    ]
  },
  {
    path: '/system/report-edit',
    component: Layout,
    hidden: true,
    children: [
      {
        path: '',
        component: () => import('@/views/system/report/edit'),
        name: 'ReportEdit',
        meta: { title: '填报模板编辑', activeMenu: '/system/report' }
      }
    ]
  },
  {
    path: '/',
    redirect: '/dashboard'
  }
]

// 动态路由，根据用户权限动态加载
// 改为空数组，完全依赖后端返回的权限列表生成路由
export const asyncRoutes = []

// 路由白名单
const whiteList = ['/login', '/auth-redirect']

// 重置路由
export function resetRouter() {
  console.log('重置路由');
  const newRouter = createRouter()
  router.matcher = newRouter.matcher // reset router
}

// 添加通配符路由
export function addNotFoundRoute() {
  console.log('添加通配符路由');
  // 检查是否已存在通配符路由
  const hasNotFoundRoute = router.getRoutes().some(route => route.path === '*');
  if (!hasNotFoundRoute) {
    // 添加通配符路由，匹配所有未定义的路由，重定向到404页面
    router.addRoute({
      path: '*',
      redirect: '/404',
      hidden: true
    });
    console.log('通配符路由已添加');
  } else {
    console.log('通配符路由已存在，无需重复添加');
  }
}

// 创建路由实例
const createRouter = () => new Router({
  // mode: 'history', // require service support
  scrollBehavior: () => ({ y: 0 }),
  routes: constantRoutes
})

const router = createRouter()

// 处理路由跳转报错
const originalPush = Router.prototype.push
Router.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') throw err
    // 处理NavigationDuplicated错误
    console.log('避免重复导航:', location)
  })
}

// 全局前置守卫
router.beforeEach(async(to, from, next) => {
  // 开始进度条
  NProgress.start()

  // 设置页面标题
  document.title = getPageTitle(to.meta.title)

  // 有token
  const hasToken = getToken()
  console.log('路由守卫检测到token:', hasToken ? '有' : '无', '目标路径:', to.path);

  if (hasToken) {
    if (to.path === '/login') {
      // 如果已登录，重定向到首页
      console.log('已登录，尝试访问登录页，重定向到首页');
      next({ path: '/' })
      NProgress.done()
    } else {
      // 确定用户是否已获取角色信息
      const hasRoles = store.getters.roles && store.getters.roles.length > 0

      if (hasRoles) {
        // 已有角色，直接通过
        console.log('已有角色信息，直接通过:', to.path);
        next()
      } else {
        try {
          console.log('尝试获取用户信息和角色权限');
          
          // 添加自动令牌验证，发送请求到后端验证token的有效性
          // 可以省略这个请求，让getInfo方法处理
          
          // 获取用户信息, 这会检查角色和权限
          const { roles, permissions } = await store.dispatch('user/getInfo')
          
          if (!permissions || permissions.length === 0) {
            console.warn('用户没有权限:', permissions);
            throw new Error('您没有任何菜单权限，请联系管理员')
          }
          
          console.log('获取到权限信息:', permissions);
          
          // 基于角色和权限生成可访问路由
          const accessRoutes = await store.dispatch('permission/generateRoutes', permissions)
          console.log('生成的可访问路由:', accessRoutes);
          
          // 动态添加可访问路由
          router.addRoutes(accessRoutes)
          
          // 添加404页面作为最后一个路由
          router.addRoutes([{ path: '*', redirect: '/404', hidden: true }])
          
          // 确保addRoutes完成
          // 设置replace: true，导航不会留下历史记录
          next({ ...to, replace: true })
        } catch (error) {
          console.error('路由守卫出错:', error);
          // 移除token并转到登录页重新登录
          await store.dispatch('user/resetToken')
          Message.error(error?.message || '出现错误，请重新登录')
          next(`/login?redirect=${to.path}`)
          NProgress.done()
        }
      }
    }
  } else {
    // 没有token
    if (whiteList.indexOf(to.path) !== -1) {
      // 在免登录白名单，直接进入
      console.log('访问白名单页面，直接通过:', to.path);
      next()
    } else {
      // 否则全部重定向到登录页
      console.log('未登录，重定向到登录页，记录重定向:', to.path);
      next(`/login?redirect=${to.path}`)
    }
  }
})

// 全局后置钩子
router.afterEach(() => {
  // 完成进度条
  NProgress.done()
})

// 初始化时不添加通配符路由，等待所有动态路由注册完成后再添加
// 移除原有的通配符路由配置

export default router